import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Text, Loader } from '@pingux/astro';
import PlusIcon from '@pingux/mdi-react/PlusIcon';
import ChevronLeftIcon from '@pingux/mdi-react/ChevronLeftIcon';
import ChevronRightIcon from '@pingux/mdi-react/ChevronRightIcon';
import CredentialList from '@components/CredentialList';
import SuperadminAuditSection from '@components/SuperadminAuditSection';
import { auditService } from '@services/audit';
import { credentialsService } from '@services/credentials';
import { AccessAuditSummaryResponse, Credential } from '@app-types/tap-partner-vault';
import { useAuthContext } from 'state/context/AuthContext';

const ITEMS_PER_PAGE = 25;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessAuditSummary, setAccessAuditSummary] = useState<AccessAuditSummaryResponse | null>(null);
  const [accessAuditLoading, setAccessAuditLoading] = useState(false);
  const [accessAuditError, setAccessAuditError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const {
    state: { user },
  } = useAuthContext();
  const userRole = user?.role ?? 'user';
  const canManage = userRole === 'admin' || userRole === 'superadmin';
  const isSuperAdmin = userRole === 'superadmin';

  const filteredCredentials = search.trim()
    ? credentials.filter((c) => {
        const q = search.toLowerCase();
        return (
          c.partner?.toLowerCase().includes(q) ||
          c.tenant_id?.toLowerCase().includes(q) ||
          c.credential_type?.toLowerCase().includes(q) ||
          c.environment?.toLowerCase().includes(q) ||
          c.account_name?.toLowerCase().includes(q) ||
          c.group_tags?.some((tag) => tag.toLowerCase().includes(q))
        );
      })
    : credentials;

  // Pagination calculations
  const totalPages = Math.ceil(filteredCredentials.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCredentials = filteredCredentials.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const fetchCredentials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await credentialsService.list();
      setCredentials(response.credentials);
    } catch (err) {
      setError('Failed to load credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAccessAudit = useCallback(async () => {
    if (!isSuperAdmin) {
      setAccessAuditSummary(null);
      setAccessAuditError(null);
      setAccessAuditLoading(false);
      return;
    }

    try {
      setAccessAuditLoading(true);
      setAccessAuditError(null);
      const response = await auditService.getAccessSummary();
      setAccessAuditSummary(response);
    } catch (_err) {
      setAccessAuditError('Failed to load the access audit log. Please try again.');
    } finally {
      setAccessAuditLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  useEffect(() => {
    fetchAccessAudit();
  }, [fetchAccessAudit]);

  const handleValidate = useCallback(async (id: string) => {
    try {
      await credentialsService.validate(id);
      await fetchCredentials();
    } catch (err) {
      setError('Failed to validate credential.');
    }
  }, [fetchCredentials]);

  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) {
      return;
    }

    try {
      await credentialsService.delete(id);
      await fetchCredentials();
    } catch (err) {
      setError('Failed to delete credential.');
    }
  }, [fetchCredentials]);

  return (
    <Box sx={{ padding: '1.5rem 0' }}>
      {/* Toolbar */}
      <div style={{ display: 'grid', gridTemplateColumns: '20rem 1fr 20rem', alignItems: 'center', marginBottom: '1rem' }}>
        <input
          type="search"
          placeholder="Search credentials…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '0.45rem 0.75rem',
            fontSize: '0.875rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            outline: 'none',
            color: '#111827',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="primary"
            onPress={() => navigate('/credentials/new')}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', width: 'fit-content' }}
          >
            <PlusIcon size={18} />
            Add Credential
          </Button>
        </div>
        <div />
      </div>

      {/* Error Message */}
      {error && (
        <Box
          sx={{
            padding: '1rem',
            backgroundColor: '#fee2e2',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            borderLeft: '4px solid #dc2626',
          }}
        >
          <Text sx={{ color: '#991b1b' }}>{error}</Text>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader />
        </Box>
      )}

      {/* Credentials List */}
      {!loading && (
        <>
          <CredentialList
            credentials={paginatedCredentials}
            onValidate={canManage ? handleValidate : undefined}
            onDelete={canManage ? handleDelete : undefined}
          />

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <Button
                variant="inline"
                onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
                isDisabled={currentPage === 1}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <ChevronLeftIcon size={18} />
                Previous
              </Button>
              <Text sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Page {currentPage} of {totalPages} ({filteredCredentials.length} credentials)
              </Text>
              <Button
                variant="inline"
                onPress={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                isDisabled={currentPage === totalPages}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
              >
                Next
                <ChevronRightIcon size={18} />
              </Button>
            </Box>
          )}

          {isSuperAdmin && (
            <SuperadminAuditSection
              summary={accessAuditSummary}
              loading={accessAuditLoading}
              error={accessAuditError}
              onRefresh={fetchAccessAudit}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
